import { useEffect, useRef, useState } from "react";
import { Outlet, useBlocker } from "react-router-dom";
import { consumeEditNavigationPermit, requestProtectedAction, runForcedEditExit, useEditProtectionState } from "@ilabel/ui";
import { editResourceKey, protectBlockedEditNavigation } from "./edit-navigation-state.js";

/** Only authentication/authorization enforcement may use this bypass. */
export function forceEditNavigation(target: string, action: () => void) {
  return runForcedEditExit(action, { navigationTarget: target });
}

export function EditProtectionRouteBoundary() {
  const state = useEditProtectionState();
  const confirming = useRef(false);
  const handledAttempts = useRef(new WeakSet<object>());
  const [settled, setSettled] = useState(0);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (editResourceKey(currentLocation) === editResourceKey(nextLocation)) return false;
    const target = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
    if (consumeEditNavigationPermit(target)) return false;
    return state.dirty || state.busy || state.outcomeUnknown;
  });
  const latest = useRef(blocker);
  latest.current = blocker;
  useEffect(() => {
    if (blocker.state !== "blocked" || confirming.current || handledAttempts.current.has(blocker)) return;
    // Router reset/proceed may render after the coordinator settles. Do not reopen
    // that same blocked attempt; a new navigation creates a new blocker object.
    handledAttempts.current.add(blocker);
    confirming.current = true;
    const target = `${blocker.location.pathname}${blocker.location.search}${blocker.location.hash}`;
    void protectBlockedEditNavigation(blocker, () => latest.current.state === "blocked" ? latest.current : null, requestProtectedAction).finally(() => {
      consumeEditNavigationPermit(target);
      confirming.current = false;
      setSettled(value => value + 1);
    });
  }, [blocker, settled]);
  return <Outlet />;
}
